import {isBusinessOwner} from '@afriex/core/utils';

import {
  Badge,
  Table,
  TableSkeleton,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import {capitalizeWords} from '@/utils/dashboard';

type Props = {
  members: AfriexBusinessUser[];
  isLoading?: boolean;
  dataCy?: string;
};

// Owner is a flag, not a role value; staffRole (Finance/Developer/Support/
// Operations) is the granular label when set, otherwise fall back to the
// coarse org role (Admin/Staff).
const teamRoleLabel = (member: AfriexBusinessUser): string => {
  if (isBusinessOwner(member)) {
    return 'Owner';
  }
  if (member.meta?.staffRole) {
    return capitalizeWords(member.meta.staffRole);
  }
  if (member.role) {
    return capitalizeWords(member.role);
  }
  return 'Member';
};

const roleColorScheme = (label: string): string => {
  switch (label.toLowerCase()) {
    case 'owner':
      return 'indigo';
    case 'admin':
      return 'blue';
    case 'finance':
      return 'green';
    case 'developer':
      return 'cyan';
    default:
      return 'gray';
  }
};

const BusinessTeamMembersTable = ({members, isLoading, dataCy}: Props) => {
  const idPrefix = dataCy ? `${dataCy}-` : '';
  if (isLoading) {
    return (
      <div className="pt-3">
        <TableSkeleton />
      </div>
    );
  }

  if (!members.length) {
    return (
      <div className="flex flex-col items-center justify-center pb-10 pt-10 text-center">
        <h3 className="mb-1 font-semibold text-gray-900 dark:text-gray-200">
          No team members
        </h3>
        <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
          This business has no additional team members yet.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-3">
      <Table className="overflow-hidden">
        <Thead>
          <Tr enableHover={false}>
            <Th className="px-4">Name</Th>
            <Th>Email Address</Th>
            <Th>Phone Number</Th>
            <Th>Team Role</Th>
          </Tr>
        </Thead>
        <TBody>
          {members.map((member, index) => {
            const roleLabel = teamRoleLabel(member);
            return (
              <Tr key={member.id ?? index}>
                <Td
                  data-cy={`${idPrefix}team-member-name`}
                  className="font-medium px-4"
                >
                  {member.name?.fullName || '—'}
                </Td>
                <Td data-cy={`${idPrefix}team-member-email`}>
                  {member.email || '—'}
                </Td>
                <Td data-cy={`${idPrefix}team-member-phone`}>
                  {member.phone || '—'}
                </Td>
                <Td>
                  <Badge
                    dataCy={`${idPrefix}team-member-role-badge`}
                    label={roleLabel}
                    colorScheme={roleColorScheme(roleLabel)}
                  />
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
};

export default BusinessTeamMembersTable;
